import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  InputAdornment,
  Chip,
  Avatar,
  MenuItem,
  Select,
  IconButton,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Box,
  Typography,
  FormControl,
  InputLabel,
  FormHelperText,
} from "@mui/material";
import { FiSearch, FiMoreHorizontal, FiPlus } from "react-icons/fi";
import { apiRequest } from "../../api/apiHelper";
import Toast from "../../components/Toast";
import useToast from "../../hooks/useToast";

const statusOptions = [
  { label: "All", value: "all" },
  { label: "Active", value: "complete" },
  { label: "Pending", value: "processing" },
];

function getStatusChip(status) {
  if (status === "complete")
    return (
      <Chip
        label="Active"
        size="small"
        style={ { background: "#e6f9f2", color: "#3ecf8e", fontWeight: 600 } }
      />
    );
  if (status === "processing")
    return (
      <Chip
        label="Pending"
        size="small"
        style={ { background: "#fff7e6", color: "#ffb300", fontWeight: 600 } }
      />
    );
  if (status === "Inactive")
    return (
      <Chip
        label="Inactive"
        size="small"
        style={ { background: "#f7f7fa", color: "#b0b7c3", fontWeight: 600 } }
      />
    );
  return null;
}

export default function Team() {
  const { toast, success, error, hideToast } = useToast();
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [teamData, setTeamData] = useState([]);
  const [loading, setLoading] = useState(true);

  // Invite Dialog State
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteList, setInviteList] = useState([]);
  const [inviteError, setInviteError] = useState("");
  const [sendingInvites, setSendingInvites] = useState(false);

  // Role Based Invite State
  const [managersList, setManagersList] = useState([]);
  const [selectedInviteRole, setSelectedInviteRole] = useState("member");
  const [selectedManagerId, setSelectedManagerId] = useState("");

  // Get user role from localStorage
  const userData = (() => {
    try {
      return JSON.parse(localStorage.getItem("userData") || "{}");
    } catch {
      return {};
    }
  })();
  const userRole = userData.role || "member";
  const userId = userData.id;

  // Fetch team members on component mount
  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const response = await apiRequest("get", "/invite/get-team-member");
      console.log("Team members response:", response);

      if (response.data?.invites && Array.isArray(response.data.invites)) {
        setTeamData(response.data.invites);
      } else {
        setTeamData([]);
      }
    } catch (err) {
      console.error("Error fetching team members:", err);
      if (err.response?.status !== 404) {
        error("Failed to fetch team members.", { title: "Error" });
      }
      setTeamData([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchManagers = async () => {
    try {
      // Only Admin needs to fetch managers list to assign
      if (userRole !== 'admin') return;

      const response = await apiRequest("get", "/admin/managers");
      console.log("Managers response:", response);
      if (response.status && response.data?.managers) {
        setManagersList(response.data.managers);
      }
    } catch (err) {
      console.error("Error fetching managers:", err);
    }
  };

  const filteredTeam = teamData.filter(
    (m) =>
      (statusFilter === "all" || m.status === statusFilter) &&
      (m.name?.toLowerCase().includes(search.toLowerCase()) ||
        m.email?.toLowerCase().includes(search.toLowerCase()))
  );

  // Invite functionality
  const handleAddInvite = () => {
    if (!inviteEmail || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(inviteEmail)) {
      setInviteError("Enter a valid email");
      return;
    }
    if (inviteList.includes(inviteEmail)) {
      setInviteError("Already added");
      return;
    }
    if (inviteList.length >= 5) {
      setInviteError("Maximum 5 invites allowed");
      return;
    }
    setInviteList([...inviteList, inviteEmail]);
    setInviteEmail("");
    setInviteError("");
  };

  const handleDeleteInvite = (email) => {
    setInviteList(inviteList.filter((e) => e !== email));
  };

  const handleSendInvites = async () => {
    let finalEmails = [...inviteList];

    // If there's an email in the input that hasn't been added to the list yet
    if (inviteEmail.trim()) {
      if (/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(inviteEmail.trim())) {
        if (!finalEmails.includes(inviteEmail.trim())) {
          finalEmails.push(inviteEmail.trim());
        }
      } else {
        setInviteError("Please enter a valid email address");
        return;
      }
    }

    if (finalEmails.length === 0) {
      setInviteError("Please add at least one email to invite");
      return;
    }

    // Validation
    if (selectedInviteRole === 'member') {
      // Manager ID required only if Admin is inviting. 
      // If Manager is inviting, we use their own ID automatically.
      if (userRole === 'admin' && !selectedManagerId) {
        setInviteError("Please select a manager");
        return;
      }
    }

    setSendingInvites(true);
    setInviteError("");

    try {
      const payload = {
        emails: finalEmails,
        role: selectedInviteRole,
      };

      // Add extra fields if role is member
      if (selectedInviteRole === 'member') {
        // Auto-assign ID for managers, use selected ID for admins
        payload.managerId = userRole === 'manager' ? userId : selectedManagerId;
      }

      console.log("Sending Invite Payload:", payload);

      const response = await apiRequest("post", "/invite/sendInvite", payload);
      console.log("Invite API response:", response);

      // Handle response 
      let successCount = 0;
      let failCount = 0;
      let results = null;

      if (response.results && Array.isArray(response.results)) {
        results = response.results;
      } else if (response.data?.results && Array.isArray(response.data.results)) {
        results = response.data.results;
      }

      if (results) {
        successCount = results.filter(r => r.status === 'invited').length;
        failCount = results.length - successCount;
      } else if (response.message) {
        successCount = finalEmails.length;
      }

      if (successCount > 0) {
        success(`Invites sent successfully!`, { title: "Success" });
        handleCloseInviteDialog();
        fetchTeamMembers();
      } else if (failCount > 0) {
        error("Failed to send invites.", { title: "Error" });
      } else {
        success("Invites processed.", { title: "Success" });
        handleCloseInviteDialog();
        fetchTeamMembers();
      }

    } catch (err) {
      console.error("Error sending invites:", err);
      const errMsg = err.response?.data?.message || err.message || "Failed to send invite";
      error(errMsg, { title: "Invite Error" });
    } finally {
      setSendingInvites(false);
    }
  };

  const handleOpenInviteDialog = () => {
    // Reset form
    setInviteList([]);
    setInviteEmail("");
    setInviteError("");
    setSelectedManagerId("");

    // Default logic
    if (userRole === 'admin') {
      setSelectedInviteRole("member");
      fetchManagers(); // Fetch managers only if admin
    } else {
      setSelectedInviteRole("member");
    }

    setInviteDialogOpen(true);
  };

  const handleCloseInviteDialog = () => {
    setInviteDialogOpen(false);
    setInviteList([]);
    setInviteEmail("");
    setInviteError("");
  };

  return (
    <div
      style={ {
        margin: "0 auto",
        background: "#fff",
        borderRadius: 12,
        paddingTop: 0,
      } }
    >
      <div
        style={ {
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        } }
      >
        <h2 style={ { fontWeight: 600, fontSize: 28, margin: 0, color: "#1e293b" } }>
          Organization
        </h2>
      </div>

      <div
        style={ {
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 24,
        } }
      >
        <div style={ { display: "flex", alignItems: "center", gap: 10 } }>
          { statusOptions.map((opt) => (
            <Button
              key={ opt.value }
              variant={ statusFilter === opt.value ? "contained" : "outlined" }
              onClick={ () => setStatusFilter(opt.value) }
              style={ {
                background: statusFilter === opt.value
                  ? (opt.value === 'complete' ? '#3ecf8e' : opt.value === 'processing' ? '#ffb300' : '#396afc')
                  : '#fff',
                color: statusFilter === opt.value
                  ? (opt.value === 'complete' || opt.value === 'processing' ? '#000' : '#fff')
                  : '#64748b',
                borderColor: statusFilter === opt.value
                  ? (opt.value === 'complete' ? '#3ecf8e' : opt.value === 'processing' ? '#ffb300' : '#396afc')
                  : '#e2e8f0',
                fontWeight: 600,
                borderRadius: 8,
                minWidth: 85,
                boxShadow: "none",
                textTransform: "none",
                padding: "6px 16px",
              } }
              size="small"
            >
              { opt.label }
            </Button>
          )) }
        </div>
        <div style={ { display: "flex", gap: 12, alignItems: "center" } }>
          <TextField
            placeholder="Search members..."
            size="small"
            value={ search }
            onChange={ (e) => setSearch(e.target.value) }
            InputProps={ {
              startAdornment: (
                <InputAdornment position="start">
                  <FiSearch style={ { color: "#94a3b8" } } />
                </InputAdornment>
              ),
              style: { borderRadius: 8, background: "#f8fafc", border: "1px solid #e2e8f0" },
            } }
            style={ { minWidth: 280 } }
          />
          {/* Show Invite Button for Admin and Manager */ }
          { ['admin', 'manager'].includes(userRole) && (
            <Button
              variant="contained"
              onClick={ handleOpenInviteDialog }
              style={ {
                background: "#396afc",
                color: "#fff",
                borderRadius: 8,
                fontWeight: 600,
                textTransform: "none",
                padding: "8px 20px",
                boxShadow: "0 1px 3px rgba(57, 106, 252, 0.2)",
              } }
            >
              <FiPlus style={ { marginRight: 8 } } />
              <span>Invite members</span>
            </Button>
          ) }
        </div>
      </div>

      <TableContainer
        component={ Paper }
        style={ {
          borderRadius: 10,
          boxShadow: "none",
          border: "1px solid #e2e8f0",
          overflow: "hidden",
        } }
        sx={ {
          '& .MuiTable-root': { borderCollapse: 'separate' },
          '& .MuiTableCell-root': { borderBottom: '1px solid #e2e8f0' },
          '& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root': { borderBottom: 'none' },
        } }
      >
        <Table>
          <TableHead>
            <TableRow style={ { background: "#f8fafc" } }>
              {/* <TableCell padding="checkbox" style={ { width: 50, paddingRight: 8 } } /> */ }
              <TableCell style={ { fontWeight: 700, color: "#334155", fontSize: 12, letterSpacing: "0.5px", paddingRight: 24, paddingLeft: 85 } }>
                NAME
              </TableCell>
              <TableCell style={ { fontWeight: 700, color: "#334155", fontSize: 12, letterSpacing: "0.5px", paddingRight: 24, paddingLeft: 115 } }>
                EMAIL
              </TableCell>
              <TableCell style={ { fontWeight: 700, color: "#334155", fontSize: 12, letterSpacing: "0.5px", paddingRight: 24, paddingLeft: 40 } }>
                ROLE
              </TableCell>
              <TableCell style={ { fontWeight: 700, color: "#334155", fontSize: 12, letterSpacing: "0.5px", paddingRight: 24, paddingLeft: 40 } }>
                STATUS
              </TableCell>
              {/* <TableCell style={ { width: 60, paddingRight: 16 } } /> */ }
            </TableRow>
          </TableHead>
          <TableBody>
            { loading ? (
              <TableRow>
                <TableCell colSpan={ 7 } align="center" style={ { padding: 40 } }>
                  <CircularProgress size={ 24 } style={ { color: "#396afc" } } />
                  <Typography style={ { marginTop: 16, color: "#64748b" } }>
                    Loading team members...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : filteredTeam.length === 0 ? (
              <TableRow>
                <TableCell colSpan={ 7 } align="center" style={ { padding: 40 } }>
                  <Typography style={ { color: "#64748b" } }>
                    { teamData.length === 0 ? "No team members found." : "No team members match your filters." }
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredTeam.map((m, i) => (
                <TableRow key={ m._id || i } hover>
                  {/* <TableCell padding="checkbox" style={ { paddingRight: 8 } }>
                    <Checkbox color="primary" />
                  </TableCell> */}
                  <TableCell style={ { paddingRight: 24, paddingLeft: 60 } }>
                    <div style={ { display: "flex", alignItems: "center", gap: 12 } }>
                      <Avatar src={ m.avatar } alt={ m.name }>
                        { !m.avatar && (m.name?.[0] || m.email?.[0] || "U") }
                      </Avatar>
                      <span style={ { fontWeight: 600, color: "#1e293b" } }>
                        { m.name || "Member" }
                      </span>
                    </div>
                  </TableCell>
                  <TableCell style={ { color: "#64748b", paddingRight: 24, paddingLeft: 80 } }>{ m.email }</TableCell>
                  <TableCell style={ { color: "#64748b", paddingRight: 24, paddingLeft: 40, textTransform: 'capitalize' } }>
                    { m.role || '-' }
                  </TableCell>
                  <TableCell style={ { paddingRight: 24, paddingLeft: 40 } }>{ getStatusChip(m.status) }</TableCell>
                  {/* <TableCell style={ { paddingRight: 16 } }>
                    <IconButton size="small">
                      <FiMoreHorizontal style={ { color: "#94a3b8" } } />
                    </IconButton>
                  </TableCell> */}
                </TableRow>
              ))
            ) }
          </TableBody>
        </Table>
      </TableContainer>

      <div style={ { display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 20 } }>
        <span style={ { color: "#64748b", fontSize: 14, fontWeight: 500 } }>
          Showing { filteredTeam.length } of { teamData.length } members
        </span>
        <Pagination
          count={ Math.ceil(filteredTeam.length / 10) || 1 }
          page={ page }
          onChange={ (_, v) => setPage(v) }
          color="primary"
          shape="rounded"
        />
      </div>

      <Toast { ...toast } onClose={ hideToast } />

      {/* Dynamic Invite Dialog */ }
      <Dialog
        open={ inviteDialogOpen }
        onClose={ handleCloseInviteDialog }
        maxWidth="sm"
        fullWidth
        PaperProps={ { style: { borderRadius: 16, padding: 8 } } }
      >
        <DialogTitle style={ { fontWeight: 700, fontSize: 22, paddingBottom: 8, color: "#1e293b" } }>
          Invite Team Members
        </DialogTitle>
        <DialogContent>
          <Box style={ { display: 'flex', flexDirection: 'column', gap: 16, paddingTop: 8 } }>

            {/* 1. Add Emails (Always Visible) */ }
            <Box>
              <Typography style={ { marginBottom: 4, fontWeight: 600, color: "#334155", fontSize: 14 } }>
                Email Addresses
              </Typography>
              <div style={ { display: "flex", gap: 8, marginBottom: 8 } }>
                <TextField
                  placeholder="Enter email address"
                  value={ inviteEmail }
                  onChange={ (e) => setInviteEmail(e.target.value) }
                  size="small"
                  style={ { flex: 1 } }
                  error={ !!inviteError && !inviteError.includes("select") }
                  InputProps={ { style: { borderRadius: 8 } } }
                  onKeyPress={ (e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddInvite();
                    }
                  } }
                />
                <Button
                  variant="contained"
                  onClick={ handleAddInvite }
                  style={ {
                    background: "#396afc", color: "#fff",
                    borderRadius: 8, fontWeight: 600, minWidth: 80,
                    textTransform: 'none'
                  } }
                  disabled={ !inviteEmail }
                >
                  Add
                </Button>
              </div>

              { inviteList.length > 0 && (
                <Box style={ { display: "flex", flexWrap: "wrap", gap: 8, background: "#f8fafc", padding: 8, borderRadius: 8 } }>
                  { inviteList.map(email => (
                    <Chip
                      key={ email } label={ email }
                      onDelete={ () => handleDeleteInvite(email) }
                      size="small"
                      style={ { background: "#fff", border: "1px solid #e2e8f0" } }
                    />
                  )) }
                </Box>
              ) }
            </Box>

            {/* 2. Role Selector (Admin Only) */ }
            { userRole === 'admin' && (
              <FormControl fullWidth size="small">
                <InputLabel id="role-select-label">Role to Assign</InputLabel>
                <Select
                  labelId="role-select-label"
                  value={ selectedInviteRole }
                  label="Role to Assign"
                  onChange={ (e) => setSelectedInviteRole(e.target.value) }
                  style={ { borderRadius: 8 } }
                >
                  <MenuItem value="member">Team Member</MenuItem>
                  <MenuItem value="manager">Manager</MenuItem>
                </Select>
              </FormControl>
            ) }

            {/* 3. Manager Assignment - Only if Role is 'member' and user is Admin */ }
            { selectedInviteRole === 'member' && userRole === 'admin' && (
              <FormControl fullWidth size="small" error={ !!inviteError && inviteError.includes("manager") }>
                <InputLabel id="manager-select-label">Assign Manager</InputLabel>
                <Select
                  labelId="manager-select-label"
                  value={ selectedManagerId }
                  label="Assign Manager"
                  onChange={ (e) => setSelectedManagerId(e.target.value) }
                  style={ { borderRadius: 8 } }
                >
                  { managersList.length > 0 ? (
                    managersList.map((mgr) => (
                      <MenuItem key={ mgr._id } value={ mgr._id }>
                        { mgr.name } ({ mgr.email })
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>No managers found</MenuItem>
                  ) }
                </Select>
                <FormHelperText>Select the manager for this member</FormHelperText>
              </FormControl>
            ) }

            { inviteError && (
              <Typography color="error" variant="body2" align="center">
                { inviteError }
              </Typography>
            ) }

          </Box>
        </DialogContent>
        <DialogActions style={ { padding: "12px 24px 20px" } }>
          <Button onClick={ handleCloseInviteDialog } style={ { color: "#64748b", textTransform: 'none' } }>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={ handleSendInvites }
            disabled={ sendingInvites || (inviteList.length === 0 && !inviteEmail.trim()) }
            style={ {
              background: "#396afc", color: "#fff",
              borderRadius: 8, fontWeight: 600,
              textTransform: 'none', minWidth: 120,
              boxShadow: "0 4px 12px rgba(57, 106, 252, 0.2)"
            } }
          >
            { sendingInvites ? <CircularProgress size={ 20 } style={ { color: '#fff' } } /> : "Send Invites" }
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
} 
